import { createColumnHelper } from '@tanstack/react-table';
import { format } from 'date-fns';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';

import { IPayment } from '@/modules/payment/types';

import CheckMark from '~/images/shared/CheckMarkWithoutBG.svg';
import Close from '~/images/shared/Close.svg';
import ExleminationMark from '~/images/shared/ExleminationMark.svg';

export const useBillingUtils = () => {
  const { t } = useTranslation('payment');

  const getInvoicesColumns = () => {
    const columnHelper = createColumnHelper<IPayment.IInvoiceSchema>();
    return [
      // columnHelper.accessor('id', {
      //   header: t('invoice'),
      //   cell: ({
      //     row: {
      //       index,
      //       original: { date },
      //     },
      //   }) => (
      //     <div className='flex items-center gap-8 text-surface-10'>
      //       {t('invoice')} #{index} - {format(new Date(date), 'MMM yyyy')}
      //     </div>
      //   ),
      // }),
      columnHelper.accessor('date', {
        header: t('billing-date'),
        cell: ({
          row: {
            original: { date },
          },
        }) => (
          <div className='flex items-center gap-8 text-surface-10'>
            {format(new Date(date), 'MMM dd, yyyy')}
          </div>
        ),
      }),
      columnHelper.accessor('subscriptionPlan.name', {
        header: t('plan'),
        cell: ({
          row: {
            original: {
              subscriptionPlan: { interval, name },
            },
          },
        }) => (
          <div className='flex items-center gap-8 text-surface-10'>
            {t(name)} ({t(interval === 'year' ? 'annually' : 'monthly')})
          </div>
        ),
      }),
      columnHelper.accessor('subscriptionPlan.numberOfFields', {
        header: t('fields'),
        cell: ({ getValue }) => (
          <div className='flex items-center gap-8 text-surface-10'>
            {getValue() ?? 0}
          </div>
        ),
      }),
      columnHelper.accessor('amount', {
        header: t('amount'),
        cell: ({
          row: {
            original: { currency, amount },
          },
        }) => (
          <div className='flex items-center gap-8 uppercase text-surface-10'>
            {t(currency)} {amount ?? 0}
          </div>
        ),
      }),
      columnHelper.accessor('status', {
        header: t('status'),
        cell: ({
          row: {
            original: { status },
          },
        }) => (
          <div className='flex items-center gap-8 uppercase text-surface-10'>
            {status === 'paid' && (
              <div className='flex items-center gap-1 rounded-lg bg-secondary-90 px-2 py-1 text-green-300'>
                <CheckMark width='20' height='20' />
                <span className='text-12 font-medium'>{t('paid')}</span>
              </div>
            )}
            {status === 'uncollectible' && (
              <div className='flex items-center gap-1 rounded-lg bg-error-90 px-2 py-1 text-error-20'>
                <Close width='10' height='10' />
                <span className='text-12 font-medium'>{t('declined')}</span>
              </div>
            )}
            {status !== 'uncollectible' && status !== 'paid' && (
              <div className='flex items-center gap-1 rounded-lg bg-states-waiting px-2 py-1 text-tertiary-40'>
                <ExleminationMark width='12' height='12' />
                <span className='text-12 font-medium'>
                  {t(`status-${status}`)}
                </span>
              </div>
            )}
          </div>
        ),
      }),
      columnHelper.display({
        id: 'download',
        cell: ({
          row: {
            original: { pdfUrl },
          },
        }) => (
          <Link href={pdfUrl} target='_blank' legacyBehavior>
            <a className='text-base font-medium text-primary-default'>
              {t('download')}
            </a>
          </Link>
        ),
      }),
    ];
  };

  return {
    getInvoicesColumns,
  };
};
